import { getApiClient } from '@src/shared/types/api-client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Subscription } from '@src/tools/key-vault/types/KeyVault'

const SCOPE = 'keyvaults'

const keysFactory = {
  all: () => [{ scope: SCOPE }] as const,
  subscriptions: () => [{ scope: SCOPE, entity: 'subscriptions' }] as const,
  list: (subscriptionId: string) => [{ scope: SCOPE, entity: 'list', subscriptionId }] as const,
}

interface KeyVaultListDto {
  keyVaultNames: string[]
}

async function getSubscriptions(): Promise<Subscription[]> {
  const apiClient = getApiClient()
  const response = await apiClient.get<Subscription[]>('/Subscriptions')
  return response.data
}

async function getKeyVaults(subscriptionId: string): Promise<string[]> {
  const apiClient = getApiClient()
  const response = await apiClient.get<KeyVaultListDto>(`/Subscriptions/${subscriptionId}/KeyVaults`)
  return response.data.keyVaultNames
}

async function refreshKeyVaultsFn(subscriptionId: string): Promise<string[]> {
  const apiClient = getApiClient()
  const response = await apiClient.post<KeyVaultListDto>(`/Subscriptions/${subscriptionId}/KeyVaults/Refresh`)
  return response.data.keyVaultNames
}

export function useSubscriptions(): [Subscription[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: keysFactory.subscriptions(),
    queryFn: getSubscriptions,
    placeholderData: [],
    staleTime: Infinity,
  })
  return [data ?? [], isFetching]
}

export function useKeyVaultsList(subscriptionId: string | null): [string[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: keysFactory.list(subscriptionId ?? ''),
    queryFn: () => getKeyVaults(subscriptionId!),
    placeholderData: [],
    enabled: !!subscriptionId,
  })
  return [data ?? [], isFetching]
}

export function useRefreshKeyVaults(subscriptionId: string | null): [() => Promise<string[]>, boolean] {
  const queryClient = useQueryClient()

  const { mutateAsync, isPending } = useMutation({
    mutationFn: () => refreshKeyVaultsFn(subscriptionId!),
    onSuccess: (data) => {
      queryClient.setQueryData(keysFactory.list(subscriptionId!), data)
    },
  })
  return [mutateAsync, isPending]
}
